import { useState } from 'react';
import { motion } from 'motion/react';
import { ImageSize } from 'web';
import { Box } from '@/UI/Box/Box';
import { Dropdown, Option } from '@/UI/Dropdown/Dropdown';
import { FlexCenVer } from '@/UI/styles';
import { NumberInput } from '@/UI/NumberInput/NumberInput';
import { LockAspectRatioButton } from '@/components/Common/LockAspectRatioButton';
import { useIsHovered } from '@/hooks/useIsHovered';
import { snakeCaseToTitleCase } from '@/utils/strings.utils';
import { FAL_ASPECT_RATIO_LABELS } from '@/components/Nodes/ModelComponents/consts/model-image-size.const';

interface ImageSizeInputProps {
  value: ImageSize;
  onChange: (value: ImageSize) => void;
  options: string[];
  disabled?: boolean;
  showMatchInput?: boolean;
  lockAspectRatioByDefault?: boolean;
  useAspectRatioLabels?: boolean;
}

// "Default" (Fal) and "Custom" both map to the free width/height mode.
const isCustomOption = (value: string) => value === 'Default' || value.toLowerCase() === 'custom';

export const ImageSizeInput = ({
  value,
  onChange,
  options,
  disabled,
  showMatchInput = true,
  lockAspectRatioByDefault = true,
  useAspectRatioLabels = false,
}: ImageSizeInputProps) => {
  const [isAspectRatioLocked, setIsAspectRatioLocked] = useState<boolean>(lockAspectRatioByDefault);
  // Re-sync the lock when the default changes (model switch); useState only reads it on first mount.
  const [prevLockDefault, setPrevLockDefault] = useState<boolean>(lockAspectRatioByDefault);
  if (prevLockDefault !== lockAspectRatioByDefault) {
    setPrevLockDefault(lockAspectRatioByDefault);
    setIsAspectRatioLocked(lockAspectRatioByDefault);
  }
  const { isHovered: isHovering, ...elementProps } = useIsHovered();

  const customOptionValue = options.find(isCustomOption) ?? 'Custom';

  const handleDropdownChange = (option: Option<string>) => {
    const newValue = option.value;
    onChange(
      isCustomOption(newValue) ? { type: 'custom', width: 1024, height: 1024 } : { type: 'built_in', value: newValue },
    );
  };

  const handleWidthChange = (newWidth: number) => {
    if (value.type === 'custom') {
      const finalWidth = Math.max(Math.round(newWidth), 1);
      const finalHeight = isAspectRatioLocked ? Math.round((finalWidth / value.width) * value.height) : value.height;
      onChange({ type: 'custom', width: finalWidth, height: finalHeight });
    }
  };

  const handleHeightChange = (newHeight: number) => {
    if (value.type === 'custom') {
      const finalHeight = Math.max(Math.round(newHeight), 1);
      const finalWidth = isAspectRatioLocked ? Math.round((finalHeight / value.height) * value.width) : value.width;
      onChange({ type: 'custom', width: finalWidth, height: finalHeight });
    }
  };

  const dropdownOptions: Option<string>[] = [
    ...(showMatchInput
      ? [
          {
            id: 'match_input',
            label: 'Match Input Image',
            value: 'match_input',
          },
        ]
      : []),
    ...options.map((option) => ({
      id: option,
      label: isCustomOption(option)
        ? 'Custom'
        : ((useAspectRatioLabels ? FAL_ASPECT_RATIO_LABELS[option] : undefined) ?? snakeCaseToTitleCase(option)),
      value: option,
    })),
  ];

  return (
    <Box data-testid="fal-image-size-input" sx={{ width: '100%' }}>
      <Dropdown
        width="100%"
        matchTriggerWidth
        value={value.type === 'custom' ? customOptionValue : value.value}
        disabled={disabled}
        onChange={handleDropdownChange}
        options={dropdownOptions}
        size="large"
      />
      {value.type === 'custom' && (
        <FlexCenVer sx={{ gap: 1, mt: 1 }} {...elementProps}>
          <NumberInput
            value={value.width}
            onSubmit={handleWidthChange}
            disabled={disabled ?? false}
            prefix="W"
            decimals={0}
          />
          <NumberInput
            value={value.height}
            onSubmit={handleHeightChange}
            disabled={disabled ?? false}
            prefix="H"
            decimals={0}
          />
          <motion.div animate={{ opacity: isHovering || isAspectRatioLocked ? 1 : 0 }}>
            <LockAspectRatioButton isLocked={isAspectRatioLocked} onChange={setIsAspectRatioLocked} />
          </motion.div>
        </FlexCenVer>
      )}
    </Box>
  );
};
